import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#placeDrawerIcons();
    this.#placeDrawerLearning();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.#placeDrawerLearning();
    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const parentSubmenu = details.parentElement?.closest('details.menu-open');
    const focusTarget =
      parentSubmenu instanceof HTMLDetailsElement && parentSubmenu !== this.refs.details
        ? parentSubmenu
        : this.refs.details;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Keep overflow lock only while a nested drill-down (not the root drawer) stays open
    if (!(parentSubmenu instanceof HTMLDetailsElement) || parentSubmenu === this.refs.details) {
      this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(focusTarget);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * Apply Drawer menu icon theme blocks (handle → icon/tone/section/highlight).
   */
  #placeDrawerIcons = () => {
    const sources = document.querySelectorAll('template.menu-drawer-icon-source[data-menu-handle]');
    if (!sources.length) return;

    const shopList = this.querySelector('.menu-drawer__section--shop .menu-drawer__menu');
    const exploreList = this.querySelector('.menu-drawer__section--explore .menu-drawer__menu');

    sources.forEach((source) => {
      if (!(source instanceof HTMLTemplateElement)) return;

      const handle = source.getAttribute('data-menu-handle');
      if (!handle) return;

      const item = this.querySelector(`li[data-drawer-link-handle="${CSS.escape(handle)}"]`);
      if (!(item instanceof HTMLElement)) return;

      const iconNode = source.content.querySelector('[data-drawer-nav-icon]');
      const existingIcon = item.querySelector('[data-drawer-nav-icon]');
      if (iconNode instanceof HTMLElement && existingIcon instanceof HTMLElement) {
        existingIcon.replaceWith(iconNode.cloneNode(true));
      }

      // Sale/Deals always keep lime highlight (don't let a false data-highlight strip Liquid classes)
      const isSaleHandle = handle === 'sale' || handle === 'deals';
      const highlight =
        source.getAttribute('data-highlight') === 'true' || isSaleHandle;
      item.classList.toggle('menu-drawer__list-item--sale', highlight);
      item.querySelectorAll('.menu-drawer__menu-item--cultiva').forEach((row) => {
        row.classList.toggle('menu-drawer__menu-item--sale', highlight);
      });

      // Match desktop primary | utility split (overrides stale theme-editor section values)
      // Shop: categories + Brands | Explore: Contact, Learning Hub, Commercial, Sale
      const utilityHandles = new Set([
        'learning-hub',
        'learning',
        'contact',
        'contact-us',
        'commercial-pricing',
        'commercial',
        'wholesale',
        'sale',
        'deals',
      ]);
      const shopHandles = new Set(['brands']);
      let section = source.getAttribute('data-section') || 'shop';
      if (utilityHandles.has(handle)) {
        section = 'explore';
      } else if (shopHandles.has(handle)) {
        section = 'shop';
      }

      if (section === 'explore' && exploreList instanceof HTMLElement && item.parentElement !== exploreList) {
        item.classList.add('menu-drawer__list-item--explore');
        // Keep sale highlight classes when relocating into Explore
        if (highlight) {
          item.classList.add('menu-drawer__list-item--sale');
          item.querySelectorAll('.menu-drawer__menu-item--cultiva').forEach((row) => {
            row.classList.add('menu-drawer__menu-item--sale');
          });
        }
        exploreList.appendChild(item);
      } else if (section === 'shop' && shopList instanceof HTMLElement && item.parentElement !== shopList) {
        item.classList.remove('menu-drawer__list-item--explore');
        shopList.appendChild(item);
      }
    });
  };

  /**
   * Inject Learning Hub mega-menu block content into drawer hosts.
   */
  #placeDrawerLearning = () => {
    const hosts = this.querySelectorAll('[data-drawer-learning-handle]');
    if (!hosts.length) return;

    const sources = document.querySelectorAll('template.mega-menu-learning-source[data-mega-menu-handle]');
    if (!sources.length) return;

    hosts.forEach((host) => {
      if (!(host instanceof HTMLElement)) return;
      if (host.querySelector('[data-mega-menu-learning]')) return;

      const handle = host.getAttribute('data-drawer-learning-handle');
      if (!handle) return;

      const source = [...sources].find((template) => {
        return template.getAttribute('data-mega-menu-handle') === handle;
      });
      if (!source) return;

      const panel = source.content.firstElementChild?.cloneNode(true);
      if (!(panel instanceof HTMLElement)) return;

      panel.classList.add('mega-menu-learning--drawer');

      const ctaLink = panel.querySelector('a.mega-menu-learning__cta');
      if (ctaLink instanceof HTMLAnchorElement && ctaLink.hasAttribute('data-mega-menu-default-link')) {
        const menuUrl = host.getAttribute('data-drawer-learning-url');
        if (menuUrl) ctaLink.href = menuUrl;
        ctaLink.removeAttribute('data-mega-menu-default-link');
      }

      host.replaceChildren(panel);
    });
  };
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
